import type { CalendarColor, CalendarFeed } from "@ronin/contracts";
import { Button, ColorSwatchPicker, calendarColors, cn, Dialog, Input, Label } from "@ronin/ui";
import { Plus, RefreshCw, Rss, Trash2 } from "lucide-react";
import { type FormEvent, useId, useState } from "react";
import { errorMessage } from "../../lib/errors.js";
import { FeedEye } from "./feed-bar.js";
import { useFeedMutations } from "./use-calendar.js";

type Mode = { view: "list" } | { view: "add" } | { view: "edit"; feed: CalendarFeed };

const time = (iso: string) =>
  new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(iso));

/** "Updated 24 Oct, 10:42" or "Last refresh failed · last success …". */
function feedStatus(feed: CalendarFeed): { text: string; failed: boolean } {
  if (feed.lastError) {
    return {
      text: `Last refresh failed${feed.lastSuccessAt ? ` · last success ${time(feed.lastSuccessAt)}` : ""}`,
      failed: true,
    };
  }
  return {
    text: feed.lastSuccessAt ? `Updated ${time(feed.lastSuccessAt)}` : "Not loaded yet",
    failed: false,
  };
}

interface FeedsDialogProps {
  open: boolean;
  feeds: readonly CalendarFeed[];
  onOpenChange: (open: boolean) => void;
}

/** Manage ICS feeds (spec03 FR10): list with eye and refresh, add, edit, remove. */
export function FeedsDialog({ open, feeds, onOpenChange }: FeedsDialogProps) {
  const [mode, setMode] = useState<Mode>({ view: "list" });
  const close = () => {
    onOpenChange(false);
    setMode({ view: "list" });
  };
  const title = mode.view === "add" ? "Add feed" : mode.view === "edit" ? "Edit feed" : "Feeds";
  const description =
    mode.view === "list"
      ? "ICS calendars, refreshed in the background every 30 minutes."
      : undefined;

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => !isOpen && close()}
      title={title}
      description={description}
    >
      {mode.view === "list" ? (
        <FeedList
          feeds={feeds}
          onAdd={() => setMode({ view: "add" })}
          onEdit={(feed) => setMode({ view: "edit", feed })}
          onClose={close}
        />
      ) : (
        <FeedForm
          key={mode.view === "edit" ? mode.feed.id : "add"}
          feed={mode.view === "edit" ? mode.feed : undefined}
          usedColors={feeds.map((f) => f.color)}
          onDone={() => setMode({ view: "list" })}
        />
      )}
    </Dialog>
  );
}

function FeedList({
  feeds,
  onAdd,
  onEdit,
  onClose,
}: {
  feeds: readonly CalendarFeed[];
  onAdd: () => void;
  onEdit: (feed: CalendarFeed) => void;
  onClose: () => void;
}) {
  const { refresh } = useFeedMutations();
  return (
    <div className="flex flex-col gap-3">
      {feeds.length === 0 ? (
        <div className="flex flex-col items-center gap-2 border border-dashed border-input px-6 py-8 text-center">
          <Rss className="size-6 text-brand" aria-hidden />
          <p className="text-sm font-semibold text-foreground">No feeds yet</p>
          <p className="text-xs text-muted-foreground">
            Add an ICS link, e.g. a work or sports calendar.
          </p>
        </div>
      ) : (
        <ul aria-label="Feeds" className="flex flex-col gap-2">
          {feeds.map((feed) => {
            const status = feedStatus(feed);
            const refreshing = refresh.isPending && refresh.variables?.id === feed.id;
            return (
              <li
                key={feed.id}
                className="group relative flex h-14 items-center gap-3 border bg-card px-3 hover:bg-muted/40"
              >
                <span
                  className={cn("size-3 shrink-0", calendarColors[feed.color].swatch)}
                  aria-hidden
                />
                <button
                  type="button"
                  onClick={() => onEdit(feed)}
                  className="flex min-w-0 flex-1 flex-col items-start text-left after:absolute after:inset-0 focus-visible:outline-none focus-visible:after:outline-2 focus-visible:after:-outline-offset-2 focus-visible:after:outline-ring"
                >
                  <span
                    className={cn(
                      "truncate text-sm font-medium",
                      feed.visible ? "text-foreground" : "text-faint-foreground",
                    )}
                  >
                    {feed.name}
                  </span>
                  <span
                    className={cn(
                      "truncate text-xs",
                      status.failed ? "text-destructive" : "text-muted-foreground",
                    )}
                  >
                    {status.text}
                  </span>
                </button>
                <div className="relative flex items-center gap-1">
                  <FeedEye feed={feed} />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={`Refresh ${feed.name} now`}
                    title="Refresh now"
                    disabled={refreshing}
                    onClick={() => refresh.mutate(feed)}
                  >
                    <RefreshCw className={cn(refreshing && "animate-spin")} />
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <div className="mt-2 flex items-center justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onClose}>
          Close
        </Button>
        <Button type="button" onClick={onAdd} data-autofocus>
          <Plus />
          Add feed
        </Button>
      </div>
    </div>
  );
}

function FeedForm({
  feed,
  usedColors,
  onDone,
}: {
  feed?: CalendarFeed;
  usedColors: readonly CalendarColor[];
  onDone: () => void;
}) {
  const id = useId();
  const { create, update, remove } = useFeedMutations();
  const firstFree =
    (Object.keys(calendarColors) as CalendarColor[]).find((c) => !usedColors.includes(c)) ??
    "cal-1";
  const [url, setUrl] = useState("");
  const [name, setName] = useState(feed?.name ?? "");
  const [color, setColor] = useState<CalendarColor>(feed?.color ?? firstFree);
  const [error, setError] = useState<string | null>(null);
  const pending = create.isPending || update.isPending;
  const canSave = name.trim() !== "" && (feed ? true : url.trim() !== "") && !pending;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!canSave) return;
    setError(null);
    try {
      if (feed) {
        await update.mutateAsync({
          id: feed.id,
          patch: { name: name.trim(), color, ...(url.trim() && { url: url.trim() }) },
        });
      } else {
        await create.mutateAsync({ url: url.trim(), name: name.trim(), color });
      }
      onDone();
    } catch (e) {
      // Shown in the dialog (FR10), e.g. "Could not load the feed: …".
      setError(errorMessage(e, "the feed"));
    }
  }

  return (
    <form className="flex flex-col gap-3" onSubmit={submit}>
      <div className="flex flex-col gap-1">
        <Label htmlFor={`${id}-url`}>
          {feed ? (
            <>
              Replace URL <span className="font-normal text-faint-foreground">(optional)</span>
            </>
          ) : (
            "ICS URL"
          )}
        </Label>
        <Input
          id={`${id}-url`}
          type="url"
          inputMode="url"
          autoComplete="off"
          spellCheck={false}
          maxLength={2048}
          value={url}
          placeholder={feed ? feed.urlPreview : "https://… or webcal://…"}
          data-autofocus={feed ? undefined : true}
          onChange={(e) => setUrl(e.target.value)}
        />
        {!feed && (
          <p className="text-xs text-faint-foreground">
            The link is kept private: it is never shown again in full.
          </p>
        )}
      </div>
      <div className="flex flex-col gap-1">
        <Label htmlFor={`${id}-name`}>Name</Label>
        <Input
          id={`${id}-name`}
          value={name}
          maxLength={100}
          placeholder="e.g. Work"
          data-autofocus={feed ? true : undefined}
          onChange={(e) => setName(e.target.value)}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <span id={`${id}-color`} className="text-xs font-medium text-muted-foreground">
          Colour
        </span>
        <ColorSwatchPicker aria-labelledby={`${id}-color`} value={color} onValueChange={setColor} />
      </div>

      {error && (
        <p
          role="alert"
          className="border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive"
        >
          {error}
        </p>
      )}
      {pending && !feed && <p className="text-xs text-dim-foreground">Loading the feed…</p>}

      <div className="mt-2 flex items-center gap-2">
        {feed && (
          <Button
            type="button"
            variant="destructive"
            size="sm"
            onClick={() => {
              remove.mutate(feed);
              onDone();
            }}
          >
            <Trash2 />
            Delete
          </Button>
        )}
        <div className="ml-auto flex items-center gap-2">
          <Button type="button" variant="ghost" onClick={onDone}>
            Back
          </Button>
          <Button type="submit" disabled={!canSave}>
            {feed ? "Save" : "Add feed"}
          </Button>
        </div>
      </div>
    </form>
  );
}
